import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Send, Mic, Paperclip } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from "../lib/api";
import nine from "../assets/icons/9.png";

import "./asha.css";
const HealthAssistantHero = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const fileInputRef = useRef(null);
  // Minimal selectedBubbleContext for send button logic
  const [selectedBubbleContext, setSelectedBubbleContext] = useState({ options: [] });
    // Handler for attach button
    const handleAttachClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
        fileInputRef.current.click();
      }
    };

    // Handler for file input change
    const handleFileChange = (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      // You can implement file upload logic here, or trigger a message with the file
      // For now, just append a message indicating a file was selected
      appendMessage({ sender: 'user', text: `Sent a file: ${file.name}` });
    };
  const { profile } = useAuth();
  const navigate = useNavigate();
  const scrollRef = useRef();

  const appendMessage = (msg) => setMessages((m) => [...m, msg]);

  useEffect(() => {
    const latest = [...messages].reverse().find((msg) => {
      const isAssistant = (msg?.sender || "").toLowerCase() === "asha";
      return isAssistant && (msg?.text || "").trim().length > 0;
    });
    if (!latest) return;

    try {
      localStorage.setItem(
        "qureo_ai_last_message",
        JSON.stringify({
          text: latest.text,
          sender: latest.sender,
          time: new Date().toISOString(),
        })
      );
    } catch {
      // Ignore storage failures silently.
    }
  }, [messages]);

  const isSymptomIntent = (text = '') => {
    const t = String(text).toLowerCase();
    const strongKeywords = [
      'headache', 'fever', 'cough', 'flu', 'chest pain', 'shortness of breath', 'difficulty breathing',
      'stomach pain', 'abdominal pain', 'vomiting', 'nausea', 'diarrhea', 'rash', 'swelling',
      'dizziness', 'faint', 'infection', 'xray', 'x-ray', 'diagnose', 'diagnosis', 'what is wrong with me'
    ];
    const weakKeywords = ['symptom', 'symptoms', 'pain', 'ache', 'cold', 'fatigue', 'weakness', 'breathing'];
    const symptomPhrases = [
      'my head hurts',
      'head is hurting',
      'i have a headache',
      'i feel dizzy',
      'i feel nauseous',
      'i have chest pain',
      'my chest hurts',
      'i can\'t breathe',
      'cant breathe',
      'hard to breathe',
      'trouble breathing',
      'short of breath',
      'high temperature',
      'running a fever',
      'my stomach hurts',
      'my throat hurts',
      'i have a rash',
      'i am vomiting',
      'i keep vomiting',
      'i have diarrhea'
    ];
    const symptomRegex = [
      /\b(i\s*have|having|got)\s+(a\s+)?(headache|fever|cough|rash|infection)\b/i,
      /\b(my\s+)?(head|chest|stomach|throat)\s+(hurts|is\s+hurting|pain)\b/i,
      /\b(can\s*not|can't|cant)\s+breathe\b/i,
      /\b(shortness\s+of\s+breath|difficulty\s+breathing)\b/i,
      /\b(what\s+is\s+wrong\s+with\s+me|diagnose\s+me)\b/i
    ];

    if (strongKeywords.some((k) => t.includes(k))) return true;
    if (symptomPhrases.some((p) => t.includes(p))) return true;
    if (symptomRegex.some((pattern) => pattern.test(t))) return true;

    const weakHits = weakKeywords.reduce((count, keyword) => {
      return count + (t.includes(keyword) ? 1 : 0);
    }, 0);

    return weakHits >= 2;
  };

  const location = useLocation();

  // send a message provided as text (used for navigated-in queries)
  const sendMessageText = async (providedText) => {
    const text = (providedText || '').trim();
    if (!text) return;

    const userMsg = { sender: 'user', text };
    appendMessage(userMsg);
    setLoading(true);

    // Prepare messages array for backend
    const payloadMessages = [...messages, userMsg].map((m) => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.text }));

    // optimistic assistant placeholder
    appendMessage({ sender: 'asha', text: '', actions: [] });

    try {
      const controller = new AbortController();
      const res = await fetch('/api/asha/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: payloadMessages, userProfile: { id: profile?._id, name: profile?.fullName, age: profile?.age, gender: profile?.gender } }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Stream request failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: true });

        // split by newline for NDJSON
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue;
          let obj = null;
          try { obj = JSON.parse(line); } catch (e) { continue; }

          if (obj.chunk) {
            // append chunk to last assistant message
            setMessages((prev) => {
              const copy = [...prev];
              // find last assistant message
              for (let i = copy.length - 1; i >= 0; i--) {
                if (copy[i].sender === 'asha') {
                  copy[i] = { ...copy[i], text: (copy[i].text || '') + obj.chunk };
                  break;
                }
              }
              return copy;
            });
          }

          if (obj.done) {
            done = true;
            const actions = obj.actions || [];

            setMessages((prev) => {
              const copy = [...prev];
              for (let i = copy.length - 1; i >= 0; i--) {
                if (copy[i].sender === 'asha') {
                  copy[i] = { ...copy[i], actions };
                  break;
                }
              }
              return copy;
            });
          }
        }

        if (readerDone) break;
      }
    } catch (err) {
      console.error('Streaming failed, falling back to non-stream:', err);
      try {
        const res2 = await api.post('/api/asha/chat', { messages: payloadMessages, userProfile: { id: profile?._id, name: profile?.fullName, age: profile?.age, gender: profile?.gender } });
        const data = res2.data;
        const reply = data?.reply || 'Sorry, something went wrong.';
        setMessages((prev) => {
          const copy = [...prev];
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].sender === 'asha') {
              copy[i] = { ...copy[i], text: reply, actions: data.actions || [] };
              break;
            }
          }
          return copy;
        });
      } catch (err2) {
        setMessages((prev) => {
          const copy = [...prev];
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].sender === 'asha') {
              copy[i] = { ...copy[i], text: 'Sorry, the assistant is unavailable.' };
              break;
            }
          }
          return copy;
        });
      }
    } finally {
      setLoading(false);
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;

    const userMsg = { sender: 'user', text };
    appendMessage(userMsg);
    setInput('');
    setLoading(true);

    // Prepare messages array for backend
    const payloadMessages = [...messages, userMsg].map((m) => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.text }));

    // optimistic assistant placeholder
    appendMessage({ sender: 'asha', text: '', actions: [] });

    try {
      const controller = new AbortController();
      const res = await fetch('/api/asha/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: payloadMessages, userProfile: { id: profile?._id, name: profile?.fullName, age: profile?.age, gender: profile?.gender } }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Stream request failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: true });

        // split by newline for NDJSON
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue;
          let obj = null;
          try { obj = JSON.parse(line); } catch (e) { continue; }

          if (obj.chunk) {
            // append chunk to last assistant message
            setMessages((prev) => {
              const copy = [...prev];
              // find last assistant message
              for (let i = copy.length - 1; i >= 0; i--) {
                if (copy[i].sender === 'asha') {
                  copy[i] = { ...copy[i], text: (copy[i].text || '') + obj.chunk };
                  break;
                }
              }
              return copy;
            });
          }

          if (obj.done) {
            done = true;
            const actions = obj.actions || [];

            setMessages((prev) => {
              const copy = [...prev];
              for (let i = copy.length - 1; i >= 0; i--) {
                if (copy[i].sender === 'asha') {
                  copy[i] = { ...copy[i], actions };
                  break;
                }
              }
              return copy;
            });
          }
        }

        if (readerDone) break;
      }
    } catch (err) {
      console.error('Streaming failed, falling back to non-stream:', err);
      try {
        const res2 = await api.post('/api/asha/chat', { messages: payloadMessages, userProfile: { id: profile?._id, name: profile?.fullName, age: profile?.age, gender: profile?.gender } });
        const data = res2.data;
        const reply = data?.reply || 'Sorry, something went wrong.';
        setMessages((prev) => {
          const copy = [...prev];
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].sender === 'asha') {
              copy[i] = { ...copy[i], text: reply, actions: data.actions || [] };
              break;
            }
          }
          return copy;
        });
      } catch (err2) {
        setMessages((prev) => {
          const copy = [...prev];
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].sender === 'asha') {
              copy[i] = { ...copy[i], text: 'Sorry, the assistant is unavailable.' };
              break;
            }
          }
          return copy;
        });
      }
    } finally {
      setLoading(false);
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      alert('Error with speech recognition. Please try again.');
    };

    recognition.start();
  };
  const onKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  // If navigated with initialQuery or vitals, send a starter message
  React.useEffect(() => {
    if (!location?.state) return;
    const s = location.state;
    let starter = s.initialQuery || '';
    if (!starter && s.vitals) {
      const v = s.vitals;
      starter = `Please summarise my latest vitals: BP ${v.bp}, HR ${v.heart} bpm, Temp ${v.temp}°F, O2 ${v.oxygen}%.`;
    }
    if (starter) {
      // small timeout so UI mounts before streaming
      setTimeout(() => sendMessageText(starter), 250);
    }
    // clear location.state to avoid re-sending if user navigates back
    // (can't mutate location, but it's fine — the effect only runs once per mount)
  }, [location?.state]);

  const handleAction = (action) => {
    if (action.type === 'book_appointment') {
      // open booking modal to choose doctor and date/time
      openBookingModal();
      return;
    }

    if (action.type === 'navigate' && action.path) {
      // navigate to the provided app route
      navigate(action.path);
      return;
    }

    if (action.type === 'seek_emergency_care') {
      alert('If this is an emergency, call your local emergency number immediately.');
      return;
    }

    if (action.type === 'book_lab_test') {
      navigate('/lab-tests');
      return;
    }
  };

  // Telemetry for actions
  const sendActionTelemetry = async (action) => {
    try {
      await api.post('/api/asha/telemetry', { action, userId: profile?._id || null, timestamp: new Date().toISOString() });
    } catch (e) {
      // ignore telemetry errors
    }
  };

  // Confirm navigation before performing it
  const [confirmAction, setConfirmAction] = useState(null); // { action }
  const onActionClick = (action) => {
    if (action.type === 'navigate') {
      setConfirmAction(action);
      return;
    }

    if (action.type === 'book_appointment') {
      // open booking modal with suggested meta if present
      openBookingModal(action.meta || null);
      sendActionTelemetry(action);
      return;
    }

    // other actions
    handleAction(action);
    sendActionTelemetry(action);
  };

  const confirmAndNavigate = () => {
    if (!confirmAction) return;
    navigate(confirmAction.path);
    sendActionTelemetry(confirmAction);
    setConfirmAction(null);
  };

  const cancelConfirm = () => setConfirmAction(null);

  // Booking modal state and helpers
  const [showBooking, setShowBooking] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  });
  const [selectedTime, setSelectedTime] = useState(() => {
    const t = new Date();
    t.setHours(t.getHours() + 2);
    return t.toTimeString().slice(0,5);
  });

  const openBookingModal = async (suggestedMeta = null) => {
    setShowBooking(true);
    try {
      const resp = await api.get('/api/doctor');
      const data = resp.data;
      const list = Array.isArray(data) ? data : [];
      setDoctors(list);
      if (suggestedMeta && suggestedMeta.doctorId) setSelectedDoctor(suggestedMeta.doctorId);
      else if (list.length > 0) setSelectedDoctor(list[0]._id || list[0].id);

      // if suggested times provided, prefill date/time with first suggestion
      if (suggestedMeta && Array.isArray(suggestedMeta.times) && suggestedMeta.times.length > 0) {
        try {
          const dt = new Date(suggestedMeta.times[0]);
          if (!Number.isNaN(dt.getTime())) {
            setSelectedDate(dt.toISOString().slice(0,10));
            setSelectedTime(dt.toTimeString().slice(0,5));
          }
        } catch (e) {
          // ignore parse errors
        }
      }
    } catch (err) {
      console.error('Failed to load doctors', err);
      setDoctors([]);
    }
  };

  const closeBookingModal = () => setShowBooking(false);

  const confirmBooking = async () => {
    try {
      const datetime = new Date(`${selectedDate}T${selectedTime}`);
      const appointmentTime = datetime.toISOString();
      const body = {
        patient: profile?._id || null,
        doctor: selectedDoctor || null,
        mode: 'video',
        appointmentTime,
        reason: 'Booked via Asha',
        patientEmail: profile?.email || undefined,
        patient_: { name: profile?.fullName }
      };

      const res = await api.post('/api/consultations', body);
      const data = res.data;
      if (!res || res.status >= 400) throw new Error(data?.message || 'Booking failed');

      appendMessage({ sender: 'asha', text: `Appointment booked for ${new Date(appointmentTime).toLocaleString()} with ${data?.doctor_?.name || 'a doctor'}.` , actions: [] });
      closeBookingModal();
      navigate('/consultations');
    } catch (err) {
      console.error('Booking failed', err);
      appendMessage({ sender: 'qureo ai', text: 'Sorry, I could not book an appointment right now. Please try again later.' });
    }
  };

  return (
    <div className = "container" style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)' }}>
    {/* Header - Consistent with other pages */}

          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <i
                  className="fi fi-br-angle-left"
                  onClick={() => navigate(-1)}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                
                    color: "black",
                   
                    fontSize: '20px',
                  }}
                ></i>
                <span style={{ fontWeight: 700, fontSize: 20, color: "#2d176a", letterSpacing: 0.2 }}>Health Assistant</span>
              </div>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "var(--surface)",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                  flexShrink: 0,
                  cursor: "pointer",
                  border: "2px solid #ece5ff"
                }}
                onClick={() => { navigate("/profile") }}
              >
                <img
                  src={profile?.avatar || "/avatar.png"}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#7c6ca7", marginTop: 2, marginLeft: 10, fontWeight: 500 }}>
              Get instant answers to your health questions
            </div>
          </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 8px',
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '38vh',
              width: '100%',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            <div
              style={{
                fontSize: '2.2rem',
                fontWeight: 700,
                background: 'linear-gradient(90deg, #a259e6 30%, var(--primary) 70%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                lineHeight: 1.15,
                marginBottom: -100,
              }}
            >
              Hi, I'm Qureo <br />
              Your personal<br />
              AI health assistant
            </div>
          </div>
        )}

        {messages.map((m, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: '0.6rem',
              display: 'flex',
              flexDirection: m.sender === 'user' ? 'row-reverse' : 'row',
              gap: '0.4rem',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: m.sender === 'user' ? '60%' : '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.8rem',
              }}
            >
              <div
                style={{
                  background: m.sender === 'user' ? 'white' : '#f0f0f0',
                  color: 'black',
                  padding: '0.5rem 0.8rem',
                  borderRadius: '12px',
                  wordWrap: 'break-word',
                  lineHeight: '1.4',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {m.text}
              </div>

              {m.actions && m.actions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', maxWidth: '100%' }}>
                  {m.actions.map((a, i) => (
                    <button
                      key={i}
                      onClick={() => onActionClick(a)}
                      style={{
                        background: 'white',
                        color: '#333',
                        border: '1px solid #667eea',
                        borderRadius: '20px',
                        padding: '0.45rem 0.8rem',
                        fontSize: '0.78rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)',
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        <div ref={scrollRef} />
      </div>


      {/* Custom input section as requested */}
      <div className="composer-shell">
        <div className="composer-row">
          <button className="composer-icon soft" onClick={handleAttachClick} aria-label="Attach file">
            <Paperclip size={16} color="#5a2ec4" strokeWidth={2} />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Talk to our health assistant..."
            className="composer-input"
          />
          <button
            className="composer-icon solid"
            onClick={() => sendMessage()}
            disabled={!input.trim() && selectedBubbleContext.options.length === 0}
            aria-label="Send message"
          >
            <Send size={16} color="#fff" strokeWidth={2} />
          </button>
          <button
            className={`composer-icon solid ${isListening ? "active" : ""}`}
            onClick={handleVoiceInput}
            aria-label="Start voice input"
          >
            <Mic size={16} color="#fff" strokeWidth={2} />
          </button>
        </div>
        <div className="composer-toggle-row">
          <button className="toggle-btn active" onClick={handleVoiceInput}>
            <Mic size={15} color="#5a2ec4" strokeWidth={2} /> Voice
          </button>
          <button className="toggle-btn" onClick={() => document.querySelector(".composer-input")?.focus()}>
            <Send size={15} color="#5a5070" strokeWidth={2} /> Type
          </button>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*,.pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

       <span className="medical-disclaimer">This is not a substitute for professional medical advice.</span>
      <span className="hipaa-note">HIPAA-compliant</span>

      {showBooking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ width: 'min(720px, 95%)', background: '#fff', borderRadius: 10, padding: 20 }}>
            <h5 style={{ marginTop: 0 }}>Book an appointment</h5>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Doctor</label>
              <select className="form-select" value={selectedDoctor || ''} onChange={(e) => setSelectedDoctor(e.target.value)}>
                <option value="">Any available</option>
                {doctors.map((d) => (
                  <option key={d._id || d.id} value={d._id || d.id}>{d.name} {d.specialty ? `— ${d.specialty}` : ''}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Date</label>
                <input className="form-control" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Time</label>
                <input className="form-control" type="time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={closeBookingModal}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmBooking}>Confirm booking</button>
            </div>
          </div>
        </div>
      )}
      {confirmAction && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ width: 'min(560px, 95%)', background: '#fff', borderRadius: 8, padding: 18 }}>
            <h5 style={{ marginTop: 0 }}>Confirm Navigation</h5>
            <p>Do you want to go to <strong>{confirmAction.label || confirmAction.path}</strong> now?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={cancelConfirm}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmAndNavigate}>Go</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthAssistantHero;
